import type { ChatWidgetProps } from '../types';
import type { WidgetConfig } from '../config/schema';
import { useShallowStable } from '../config/stable';

interface UseWidgetBehaviorOptions {
  props: ChatWidgetProps;
  config: WidgetConfig;
  sessionHistoryEnabled: boolean;
}

/**
 * Behaviour, layout, limits and complex config objects, read from the canonical
 * config. Merged objects keep a stable identity while their contents are equal.
 */
export function useWidgetBehavior({
  props,
  config,
  sessionHistoryEnabled,
}: UseWidgetBehaviorOptions) {
  const { behavior, limits, security, sessions, endpoints } = config;

  const availability = useShallowStable(behavior.availability);
  const sessionConfig = useShallowStable(sessions.config);
  const autoOpenRules = useShallowStable(behavior.autoOpenRules);
  const preChatForm = useShallowStable(behavior.preChatForm);
  const consent = useShallowStable(security.consent);
  const idleTimeout = useShallowStable(sessions.idleTimeout);

  return {
    subjectProp: behavior.subject,
    channel: behavior.channel,
    threadIdProp: behavior.threadId,
    threadIdControlled: props.threadId !== undefined,
    threadPollIntervalMs: behavior.threadPollIntervalMs,
    department: behavior.department,
    customFields: behavior.customFields,
    records: behavior.records,
    persistThread: sessions.persistThread,
    effectivePersistThread: sessionHistoryEnabled ? false : sessions.persistThread,
    position: behavior.position,
    zIndex: behavior.zIndex,
    showViewTabs: behavior.showViewTabs,
    recordNavigation: behavior.recordNavigation,
    showTimestamps: behavior.showTimestamps,
    maxRenderedMessages: behavior.maxRenderedMessages,
    maxInputLength: limits.maxInputLength,
    inputWarningThreshold: limits.inputWarningThreshold,
    maxMessagesPerSession: limits.maxMessagesPerSession,
    rateLimit: limits.rateLimit,
    maxFileSizeMB: limits.maxFileSizeMB,
    maxFileCount: limits.maxFileCount,
    allowedFileTypes: limits.allowedFileTypes,
    allowedDomains: security.allowedDomains,
    allowedOrigins: security.allowedOrigins,
    availability,
    offlineMessage: behavior.offlineMessage,
    sessionConfig,
    autoOpenRules,
    autoOpenDelayMs: behavior.autoOpenDelayMs,
    preChatForm,
    consent,
    doNotTrain: security.doNotTrain,
    retentionDays: security.retentionDays,
    sessionVariables: sessions.variables,
    retryPolicy: endpoints.retryPolicy,
    lazyLoad: behavior.lazyLoad,
    idleTimeout,
  };
}
